import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useAwardTender, useTenderOffers } from '@/api/sourcing/sourcing.hooks';
import type {
  OfferLine,
  TenderStatus,
} from '@/api/sourcing/sourcing.types';
import { DsButton } from '../../ds';
import { fmtDate, fmtEUR } from '../helpers';

interface Props {
  tenderId: string;
  status: TenderStatus;
}

/** Cheapest offer wins; ties broken by the earlier delivery date. */
function bestOfferId(offers: OfferLine[]): string | null {
  if (offers.length === 0) return null;
  return [...offers].sort((a, b) => {
    if (a.unitPrice !== b.unitPrice) return a.unitPrice - b.unitPrice;
    return (
      new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime()
    );
  })[0].id;
}

export function OfferMatrix({ tenderId, status }: Props) {
  const { data, isPending } = useTenderOffers(tenderId);
  const award = useAwardTender();
  const { enqueueSnackbar } = useSnackbar();
  const [picks, setPicks] = useState<Map<string, string>>(new Map());

  const canAward = status === 'CLOSED';
  const isAwarded = status === 'AWARDED';

  const offerMap = useMemo(() => {
    const m = new Map<string, OfferLine>();
    for (const o of data?.offers ?? []) {
      m.set(`${o.tenderLineId}:${o.participantId}`, o);
    }
    return m;
  }, [data]);

  const bestByLine = useMemo(() => {
    const m = new Map<string, string>();
    for (const line of data?.lines ?? []) {
      const lineOffers = (data?.offers ?? []).filter(
        (o) => o.tenderLineId === line.id,
      );
      const best = bestOfferId(lineOffers);
      if (best) m.set(line.id, best);
    }
    return m;
  }, [data]);

  if (isPending || !data) {
    return <div className="sc-empty">Lädt…</div>;
  }
  const { lines, participants } = data;

  if (status === 'DRAFT') {
    return (
      <p className="sc-hint">
        Angebote sind erst nach der Veröffentlichung sichtbar.
      </p>
    );
  }
  if (participants.length === 0 || lines.length === 0) {
    return <p className="sc-hint">Keine Lieferanten oder Artikel.</p>;
  }

  const togglePick = (lineId: string, offerId: string) => {
    if (!canAward) return;
    setPicks((prev) => {
      const next = new Map(prev);
      if (next.get(lineId) === offerId) next.delete(lineId);
      else next.set(lineId, offerId);
      return next;
    });
  };

  const submitAward = () => {
    if (picks.size === 0) return;
    award.mutate(
      {
        id: tenderId,
        body: {
          awards: [...picks].map(([tenderLineId, offerLineId]) => ({
            tenderLineId,
            offerLineId,
          })),
        },
      },
      {
        onSuccess: () =>
          enqueueSnackbar(`Zuschlag für ${picks.size} Position(en) erteilt.`, {
            variant: 'success',
          }),
      },
    );
  };

  return (
    <section className="sc-section">
      <div className="ds-pane-header">
        <h3>Angebotsvergleich</h3>
        {canAward && (
          <DsButton
            variant="primary"
            size="small"
            disabled={picks.size === 0 || award.isPending}
            onClick={submitAward}
          >
            Zuschlag erteilen ({picks.size})
          </DsButton>
        )}
      </div>
      <div className="sc-matrix-wrap ds-scroll">
        <table className="ds-table sc-matrix">
          <thead>
            <tr>
              <th className="sc-matrix-corner">Artikel</th>
              {participants.map((p) => (
                <th key={p.id}>
                  {p.supplierName}
                  {!p.hasResponded && (
                    <span className="sc-matrix-noresp"> · offen</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td className="sc-matrix-line">
                  <span className="sc-matrix-line-name">{line.itemName}</span>
                  <span className="sc-matrix-line-sub">
                    {line.itemId} · Menge {line.quantity}
                  </span>
                </td>
                {participants.map((p) => {
                  const offer = offerMap.get(`${line.id}:${p.id}`);
                  const isBest =
                    offer != null && bestByLine.get(line.id) === offer.id;
                  const isPicked = picks.get(line.id) === offer?.id;
                  const isWinner =
                    isAwarded &&
                    offer != null &&
                    line.awardedOfferLineId === offer.id;
                  const cls = [
                    'sc-matrix-cell',
                    offer ? 'has-offer' : 'no-offer',
                    isBest && 'is-best',
                    isPicked && 'is-picked',
                    isWinner && 'is-winner',
                    canAward && offer && 'is-clickable',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <td
                      key={p.id}
                      className={cls}
                      onClick={() =>
                        offer && togglePick(line.id, offer.id)
                      }
                    >
                      {offer ? (
                        <>
                          <span className="sc-matrix-price">
                            {fmtEUR(offer.unitPrice)}
                          </span>
                          <span className="sc-matrix-detail">
                            {offer.quantityAvailable} Stk ·{' '}
                            {fmtDate(offer.deliveryDate)}
                          </span>
                          {isWinner && (
                            <span className="ds-pill pos">Zuschlag</span>
                          )}
                          {isPicked && !isWinner && (
                            <span className="ds-pill warn">gewählt</span>
                          )}
                        </>
                      ) : (
                        <span className="sc-matrix-empty">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canAward && (
        <p className="sc-hint">
          Klicke eine Angebotszelle an, um sie als Gewinner zu wählen. Der
          günstigste Preis pro Position ist hervorgehoben.
        </p>
      )}
    </section>
  );
}
